import * as React from "react";
import { ErrorBoundaryState, ErrorBoundaryProps } from "./ErrorBoundaryType";

const initialState: ErrorBoundaryState = { error: null };

const changedArray = (a: Array<unknown> = [], b: Array<unknown> = []) =>
	a.length !== b.length ||
	a.some((item, index) => !Object.is(item, b[index]));

export default class ErrorBoundary extends React.Component<
	React.PropsWithRef<React.PropsWithChildren<ErrorBoundaryProps>>,
	ErrorBoundaryState
> {
	static getDerivedStateFromError(error: Error) {
		return { error };
	}

	state = initialState;
	updatedWithError = false;
	resetErrorBoundary = (...args: Array<unknown>) => {
		this.props.onReset?.(...args);
		this.reset();
	};

	reset() {
		this.updatedWithError = false;
		this.setState(initialState);
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		this.props.onError?.(error, info);
	}

	componentDidMount() {
		const { error } = this.state;

		if (error !== null) {
			this.updatedWithError = true;
		}
	}

	componentDidUpdate(prevProps: ErrorBoundaryProps) {
		const { error } = this.state;
		const { resetKeys } = this.props;

		// There's an edge case where if the thing that triggered the error
		// happens to *also* be in the resetKeys array, we'd end up resetting
		// the error boundary immediately. This would likely trigger a second
		// error to be thrown.
		// So we make sure that we don't check the resetKeys on the first call
		// of cDU after the error is set
		if (error !== null && !this.updatedWithError) {
			this.updatedWithError = true;
			return;
		}

		if (error !== null && changedArray(prevProps.resetKeys, resetKeys)) {
			this.props.onResetKeysChange?.(prevProps.resetKeys, resetKeys);
			this.reset();
		}
	}

	render() {
		const { error } = this.state;

		const { fallbackRender, FallbackComponent, fallback } = this.props;

		if (error !== null) {
			const props = {
				error,
				resetErrorBoundary: this.resetErrorBoundary,
			};
			if (React.isValidElement(fallback)) {
				return fallback;
			} else if (typeof fallbackRender === "function") {
				return fallbackRender(props);
			} else if (FallbackComponent) {
				return <FallbackComponent {...props} />;
			} else {
				throw new Error(
					"react-error-boundary requires either a fallback, fallbackRender, or FallbackComponent prop"
				);
			}
		}

		return this.props.children;
	}
}
